/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase, {
  BORDER_WIDTH,
  type ComponentStyles,
} from './WireframeBase';
import tools from '../tools';
import wireframeAvatar from '../assets/icons/wireframeAvatar.svg';
import wireframeStyleAvatarRound from '../assets/icons/wireframeStyleAvatarRound.svg';
import wireframeStyleAvatarSquare from '../assets/icons/wireframeStyleAvatarSquare.svg';
import { SPACER_1, SPACER_2 } from '../utils/style';
import { WIREFRAME_PRIMARY_COLOR } from '../utils/wireframes';
import {
  FlexColumn,
  type FlexColumnProps,
} from '@lib/canvasx/components/FlexColumn';
import { Rect } from '@lib/canvasx/components/Rect';
import { Label } from '../components/wireframes/Label';
import { FlexRow, type FlexRowProps } from '@lib/canvasx/components/FlexRow';
import { type IconData } from '../types';
import { SvgIcon } from '../components/wireframes/SvgIcon';

const CONTAINER_SIZE = 52;
const ICON_SIZE = 18;
const ROUND_RADIUS = CONTAINER_SIZE;
const SQUARE_RADIUS = SPACER_1;

class WireframeAvatar extends WireframeBase {
  constructor(fabricObject) {
    super(fabricObject);
    if (!this.fabricObject.style) {
      this.fabricObject.set('style', 'round');
    }

    if (!this.label) {
      this.addLabel({ value: 'Label', position: 'noLabel' });
    }

    if (!this.placeholder) {
      this.addPlaceholder({
        value: '',
        iconLeft: { prefix: 'fas', iconName: 'user' } as IconData,
      });
    }
  }

  tools() {
    return [
      [tools.ToolWireframeComponentStyle],
      [tools.ToolPillStyleFlags],
      [tools.ToolWireframeLabelPosition],
      [tools.ToolSize],
      [tools.ToolWireframeIcon],
      [tools.ToolFillColorWithoutOpacity],
      [tools.ToolWireframeTextColor],
    ];
  }

  static iconSvg() {
    return wireframeAvatar;
  }

  static externalName(): string {
    return 'Avatar';
  }

  static componentStyles(): ComponentStyles {
    return {
      round: wireframeStyleAvatarRound,
      square: wireframeStyleAvatarSquare,
    };
  }

  componentStyles(): ComponentStyles {
    return WireframeAvatar.componentStyles();
  }

  static labelPositions(): string[] {
    return ['top', 'bottom', 'left', 'right', 'noLabel'];
  }

  labelPositions(): string[] {
    return WireframeAvatar.labelPositions();
  }

  supportedIcons(): string[] {
    return ['iconLeft'];
  }

  get labelIsLeftTop(): boolean {
    return ['left', 'top'].includes(this.labelPosition());
  }

  get labelIsVertical(): boolean {
    return ['top', 'bottom'].includes(this.labelPosition());
  }

  render(ctx: CanvasRenderingContext2D) {
    const { scaleX, showBackground, showOutline, textColor } =
      this.fabricObject;
    const icon = this.placeholder?.iconLeft;
    const { labelIsLeftTop, labelIsVertical } = this;
    const fill = this.fabricObject.fill ?? WIREFRAME_PRIMARY_COLOR;
    const borderRadius = this.round ? ROUND_RADIUS : SQUARE_RADIUS;
    const FlexElement = labelIsVertical ? FlexColumn : FlexRow;
    const flexProps = {};

    if (labelIsVertical) {
      (flexProps as FlexColumnProps).horizontalAlignment = 'center';
    } else {
      (flexProps as FlexRowProps).verticalAlignment = 'center';
    }

    let renderedDimensions: DimensionStruct;

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: scaleX,
      },
      () => {
        renderElement(
          <FlexElement
            gap={SPACER_2}
            setRenderedDimensions={(dimensions: DimensionStruct) => {
              renderedDimensions = dimensions;
            }}
            {...flexProps}
          >
            {this.showLabel && labelIsLeftTop && (
              <Label wireframe={this} color={textColor} />
            )}
            <Rect
              border={
                showOutline
                  ? { color: fill, opacity: 0.34, lineWidth: BORDER_WIDTH }
                  : {}
              }
              fill={
                showBackground ? { color: fill, opacity: 0.08 } : 'transparent'
              }
              borderRadius={borderRadius}
              width={CONTAINER_SIZE}
              height={CONTAINER_SIZE}
            >
              {icon && (
                <Rect
                  x={(CONTAINER_SIZE - ICON_SIZE) / 2}
                  y={(CONTAINER_SIZE - ICON_SIZE) / 2}
                >
                  <SvgIcon iconData={icon} size={ICON_SIZE} iconFill={fill} />
                </Rect>
              )}
            </Rect>
            {this.showLabel && !labelIsLeftTop && (
              <Label wireframe={this} color={textColor} />
            )}
          </FlexElement>
        );

        this._updateRenderedDimensions(renderedDimensions);
      }
    );
  }
}

export default WireframeAvatar;
